import { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";
import { useLocation } from "react-router-dom";
import IOSInstallGuide from "./IOSInstallGuide";
import {
  canInstall,
  isStandalone,
  onInstallAvailableChange,
  promptInstall,
} from "@/lib/pwa";

const DISMISS_KEY = "wallweek.installPromptDismissedAt";
const DISMISS_DAYS = 7;

function isRecentlyDismissed() {
  try {
    const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (!ts) return false;
    const daysSince = (Date.now() - ts) / (1000 * 60 * 60 * 24);
    return daysSince < DISMISS_DAYS;
  } catch {
    return false;
  }
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

export default function InstallPrompt() {
  const [available, setAvailable] = useState(canInstall());
  const [dismissed, setDismissed] = useState(isRecentlyDismissed());
  const [showIOSHint, setShowIOSHint] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const off = onInstallAvailableChange(setAvailable);
    // iOS Safari never fires beforeinstallprompt: show a manual hint instead.
    if (!isStandalone() && isIOS() && !isRecentlyDismissed()) {
      setShowIOSHint(true);
    }
    return () => off && off();
  }, []);

  if (isStandalone() || dismissed || pathname.startsWith("/crea")) return null;
  if (!available && !showIOSHint) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {}
    setDismissed(true);
  };

  const install = async () => {
    const res = await promptInstall();
    if (res?.outcome === "accepted") {
      setDismissed(true);
    }
  };

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed inset-x-3 bottom-3 z-[9999] mx-auto max-w-md rounded-2xl border border-white/20 bg-[#014e86]/95 px-4 py-3 text-white shadow-2xl backdrop-blur-md sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2"
      style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
    >
      {guideOpen && <IOSInstallGuide onClose={() => setGuideOpen(false)} />}
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={() => showIOSHint && setGuideOpen(true)}
          data-testid="pwa-install-open-guide"
          className="flex min-w-0 flex-1 items-start gap-3 text-left"
        >
        <img
          src="/wallweek-logo-192.png"
          alt="Wall Week"
          className="h-10 w-10 flex-shrink-0 rounded-lg object-cover"
        />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold leading-tight">
            Installa Wall Week
          </div>
          <div className="mt-0.5 text-xs opacity-90 leading-snug">
            {showIOSHint ? (
              <span className="inline-flex items-center gap-1">
                Tocca <Share className="inline h-3.5 w-3.5" /> e scegli
                <span className="font-semibold"> Aggiungi a Home</span>
              </span>
            ) : null}
            {showIOSHint ? (
              <span className="mt-1.5 block w-fit rounded-full bg-white px-3 py-1 text-[11px] font-bold text-[#014e86]">Mostrami come</span>
            ) : (
              <>Aggiungila alla schermata Home per usarla come un'app.</>
            )}
          </div>
        </div>
        </button>
        <button
          type="button"
          aria-label="Chiudi"
          data-testid="pwa-install-dismiss"
          onClick={dismiss}
          className="ml-1 flex-shrink-0 rounded-full p-1 text-white/70 hover:bg-white/10 hover:text-white"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      {available && !showIOSHint && (
        <button
          type="button"
          data-testid="pwa-install-button"
          onClick={install}
          className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-white px-4 py-2 text-sm font-semibold text-[#014e86] shadow-sm transition hover:bg-white/90 active:scale-[0.98]"
        >
          <Download className="h-4 w-4" />
          Installa app
        </button>
      )}
    </div>
  );
}
